import React from "react";
import { Tooltip } from "antd";

import {
  describeReturnTier,
  estimateReturnCharge,
  formatMiles,
  formatMins,
  formatMoney,
  metersToMiles,
  RETURN_TIER
} from "helper/returnPricing";

const Charge = ({ label, charge, rate }) => {
  if (!rate) return null;
  const tier =
    charge.tier === RETURN_TIER.NONE
      ? "not billed per mile"
      : describeReturnTier(charge.tier, rate);
  return (
    <Tooltip
      title={
        <>
          <div>
            Distance: {charge.distance.toFixed(2)} mi × {formatMoney(charge.rate)}/mi ={" "}
            {formatMoney(charge.distanceCharge)}
          </div>
          <div>Tier: {tier}</div>
          <div>
            Return time × {formatMoney(rate.pricePerReturnTime)}/hr ={" "}
            {formatMoney(charge.timeCharge)}
          </div>
        </>
      }
    >
      <span>
        {label} <span className="return-impact-price">{formatMoney(charge.total)}</span>
      </span>
    </Tooltip>
  );
};

/**
 * Per-route summary of the return-to-pickup leg's distance / time and money, plus what the
 * separate "return failed packages" compliance process means for this route.
 *
 * By contract the return-distance rate is always billed (subject to the Free/Short/Long
 * tiers) - `returnToPickupEnabled` is compliance-only and never suppresses this charge, so the
 * distance/charge here is shown regardless of that flag.
 *
 * Expects the route fields the reducer stamps from `mapValueCalculation`:
 * returnDistance (m), returnMins, failedReturnDistance (m), failedReturnMins,
 * failedReturnTarget, failedReturnAddress, failedReturnReason, pickupCount.
 */
const ReturnImpactSummary = ({
  route,
  returnToPickupEnabled,
  returnOnFailedEnabled,
  clientRate,
  driverRate,
  compact = false
}) => {
  if (!route) return null;
  const returnMeters = Number(route.returnDistance) || 0;
  const returnMins = Number(route.returnMins) || 0;
  const returnMiles = metersToMiles(returnMeters);
  const returnHours = returnMins / 60;

  const failedMeters = route.failedReturnDistance;
  const failedMins = route.failedReturnMins;
  const hasFailedLeg = failedMeters !== null && failedMeters !== undefined;
  const failedMiles = hasFailedLeg ? metersToMiles(failedMeters) : 0;
  const failedHours = hasFailedLeg ? (Number(failedMins) || 0) / 60 : 0;
  const failedTarget =
    route.failedReturnTarget === "pickup"
      ? "the pickup location"
      : route.failedReturnAddress || "the Return Location";
  const failedReason =
    route.failedReturnReason ||
    (Number(route.pickupCount) > 1
      ? "Cannot be shown for routes with more than 1 pickup."
      : "Return leg not available for this route.");

  const tierText = (miles, rate) =>
    rate ? describeReturnTier(estimateReturnCharge(miles, 0, rate).tier, rate) : null;

  return (
    <div className={`return-impact${compact ? " return-impact--compact" : ""}`}>
      <div className="return-impact-line">
        <span className="return-impact-label">Return to pickup:</span>
        <span>
          {formatMiles(returnMeters)} / {formatMins(returnMins)}
        </span>
        {clientRate && (
          <Charge
            label="· Client"
            charge={estimateReturnCharge(returnMiles, returnHours, clientRate)}
            rate={clientRate}
          />
        )}
        {driverRate && (
          <Charge
            label="· Driver"
            charge={estimateReturnCharge(returnMiles, returnHours, driverRate)}
            rate={driverRate}
          />
        )}
        {clientRate && (
          <span className="return-impact-tier">
            ({tierText(returnMiles, clientRate)})
          </span>
        )}
        {!returnToPickupEnabled && (
          <span className="return-impact-muted">
            {" "}
            (process off — compliance only, still billed)
          </span>
        )}
      </div>

      <div className="return-impact-line">
        <span className="return-impact-label">If any failed stops:</span>
        {!returnOnFailedEnabled ? (
          <span className="return-impact-muted">
            return of failed packages is off — packages stay with the driver
          </span>
        ) : hasFailedLeg ? (
          <>
            <span>
              +{formatMiles(failedMeters)} / {formatMins(failedMins)} to {failedTarget}
            </span>
            {clientRate && (
              <Charge
                label="· Client est."
                charge={estimateReturnCharge(failedMiles, failedHours, clientRate)}
                rate={clientRate}
              />
            )}
            {driverRate && (
              <Charge
                label="· Driver est."
                charge={estimateReturnCharge(failedMiles, failedHours, driverRate)}
                rate={driverRate}
              />
            )}
          </>
        ) : (
          <span className="return-impact-muted">{failedReason}</span>
        )}
      </div>
    </div>
  );
};

export default ReturnImpactSummary;
